import "babylonjs-loaders";

import * as Animation from "./animation";
import * as Audio from "./audio";
import * as Camera from "./camera";
import * as Config from "./config";

import {
  AnimationGroup,
  AssetContainer,
  BackgroundMaterial,
  Color3,
  GlowLayer,
  LensFlare,
  LensFlareSystem,
  Mesh,
  Node,
  PBRMaterial,
  SceneLoader,
  StandardMaterial,
  Tags,
  TransformNode,
  Vector3,
} from "babylonjs";

import VueStore from "@/store";
import { addShadowCaster } from "./lighting";
import store from "./store";
import { stringContainsArrayElement } from "./helpers";

let block: Mesh;
let cameraPosNodes: Node[];

const animating = false;

let currentTreeLevel = -1;
let currentTreeFrame = 2;
let treeAnimationGroup: AnimationGroup;

// Just using shortened versions because indexOf will still work and there's quite a few, eg SunFlower_Lv007
const meshesWithAlpha = [
  "EggPlant",
  "Tomato",
  "Carrot",
  "SunFlower",
  "Object001",
];

function create() {
  const gl = new GlowLayer("glow", store.scene, {
    mainTextureFixedSize: 256,
  });

  gl.customEmissiveColorSelector = (mesh, subMesh, material, result) => {
    if (Tags.MatchesQuery(mesh, "Moon")) {
      result.set(1, 1, 1, 1);
    } else if (Tags.MatchesQuery(mesh, "GodRay")) {
      result.set(0.2, 0.2, 0.2, 0.2);
    } else {
      result.set(0, 0, 0, 0);
    }
  };

  gl.intensity = 1;

  SceneLoader.LoadAssetContainer(
    "/assets/models/",
    "background.glb",
    store.scene,
    (container) => {
      container.meshes.forEach((mesh) => {
        mesh.receiveShadows = true;

        if (mesh.name.indexOf("tree") > -1) {
          addShadowCaster(mesh as Mesh);
        }

        if (mesh.material) {
          const backgroundMaterial = new BackgroundMaterial(
            "backgroundMaterial",
            store.scene
          );

          backgroundMaterial.diffuseTexture = mesh.material.getActiveTextures()[0];
          backgroundMaterial.shadowLevel = 0.3;

          mesh.material = backgroundMaterial;
        }
      });

      container.animationGroups.forEach((animationGroup) => {
        treeAnimationGroup = animationGroup;
        treeAnimationGroup.stop();

        setTreeLevel(2, false);
      });

      container.addAllToScene();
    }
  );

  SceneLoader.LoadAssetContainer(
    "/assets/models/",
    "moon.glb",
    store.scene,
    (container) => {
      container.meshes.forEach((mesh) => {
        if (mesh.name === "GeoSphere001") {
          Tags.AddTagsTo(mesh, "Moon");

          mesh.material = new PBRMaterial("Moon", store.scene);

          // (mesh.material as PBRMaterial).albedoColor = Color3.White();
          (mesh.material as PBRMaterial).emissiveColor = Color3.White();

          const system = new LensFlareSystem("LensFlare", mesh, store.scene);
          new LensFlare(
            1,
            1,
            new Color3(1, 1, 1),
            "assets/images/flair 02.jpg",
            system
          );

          new LensFlare(
            1,
            1,
            new Color3(1, 1, 1),
            "assets/images/flair 01.jpg",
            system
          );
        }
      });

      container.addAllToScene();
    }
  );

  SceneLoader.LoadAssetContainer(
    "/assets/models/",
    "cameras.glb",
    store.scene,
    (container) => {
      container.transformNodes;
      cameraPosNodes = container.transformNodes;

      Camera.setDefaultPosition(
        (cameraPosNodes.find(
          (node) => node.name === "Camera main"
        ) as TransformNode).getAbsolutePosition()
      );

      const defaultTarget = (cameraPosNodes.find(
        (node) => node.name === "Camera main.Target"
      ) as TransformNode).getAbsolutePosition();

      if (VueStore.state.isMobile && !VueStore.state.isResultsPage) {
        // 2
        defaultTarget.addInPlace(new Vector3(0, -5, 0));
        // was 10
        console.log("isMobile and not results");
      }

      Camera.setDefaultTarget(defaultTarget);

      if (VueStore.state.isDev) {
        container.addAllToScene();
      }
    }
  );
}

function clearAnswers() {
  answerArray.forEach((answer) => {
    answer.dispose();
  });
  answerArray = [];
}

let answerArray: Mesh[] = [];

function processAnswer(
  modelAssetPath: string,
  buildAssetPath: string,
  answerNumber: number,
  automaticallyResetCamera = true,
  performCameraAnimation = true
) {
  Animation.setAnimating(true);

  // Technically the actual animating in doesn't happen till the assets are loaded.

  const answerCameraPosition = (cameraPosNodes.find(
    (node) => node.name === `Camera 0${answerNumber + 1}`
  ) as TransformNode)?.getAbsolutePosition();

  const answerCameraTarget = (cameraPosNodes.find(
    (node) => node.name === `Camera 0${answerNumber + 1}.Target`
  ) as TransformNode)?.getAbsolutePosition();

  const animateCamera = answerCameraPosition && answerCameraTarget;

  if (performCameraAnimation) {
    if (animateCamera) {
      Animation.setAnimationStatus(Animation.AnimationStatus.AnimatingIn);
    }
  }

  Promise.all([getAsset(modelAssetPath), getBuildAsset(buildAssetPath)])
    .then(([assetContainer, buildAssetContainer]) => {
      // Load asset container.
      const rootMesh = assetContainer.meshes.find(
        (mesh) => mesh.name === "__root__"
      );

      answerArray.push(rootMesh as Mesh);

      if (answerNumber === 3) {
        const blockMesh = assetContainer.meshes.find(
          (mesh) => mesh.name === "block"
        );

        if (blockMesh) {
          block = blockMesh as Mesh;
        }
      } else if (answerNumber === 4 && block) {
        // Remove the block if one of the following models is being loaded.

        if (
          modelAssetPath.indexOf("Everyone_must_recycle") > -1 ||
          modelAssetPath.indexOf("Extra_food_for_everyone") > -1
        ) {
          block.visibility = 0;
        }
      }

      if (rootMesh) {
        Animation.animatePropertyVector3(
          rootMesh,
          "scaling",
          Config.SCENE_BUILD_ANIMATION_LENGTH,
          new Vector3(rootMesh.scaling.x, 0, rootMesh.scaling.z),
          rootMesh.scaling
        );
      }

      assetContainer.meshes.forEach((mesh) => {
        mesh.receiveShadows = true;
        addShadowCaster(mesh as Mesh);

        if (mesh.material) {
          const backgroundMaterial = new BackgroundMaterial(
            "backgroundMaterial",
            store.scene
          );

          backgroundMaterial.diffuseTexture = mesh.material.getActiveTextures()[0];

          if (stringContainsArrayElement(mesh.name, meshesWithAlpha)) {
            backgroundMaterial.diffuseTexture.hasAlpha = true;
          }

          backgroundMaterial.shadowLevel = 0.3;

          backgroundMaterial.useRGBColor = false;
          backgroundMaterial.primaryColor = (mesh.material as PBRMaterial).albedoColor;

          mesh.material = backgroundMaterial;
        }
      });

      assetContainer.animationGroups.forEach((animationGroup) => {
        animationGroup.play(true);
      });

      assetContainer.addAllToScene();

      // Load build asset.
      buildAssetContainer.meshes.forEach((mesh) => {
        Tags.AddTagsTo(mesh, "GodRay");
      });

      buildAssetContainer.materials.forEach((material) => {
        material.backFaceCulling = false;
        (material as StandardMaterial).emissiveColor = new Color3(1, 1, 1);
      });

      buildAssetContainer.animationGroups.forEach((animationGroup) => {
        animationGroup.reset();
        animationGroup.play(false);
      });

      if (performCameraAnimation) {
        Audio.playSFX(Audio.SoundEffects.GodRay);
      }

      buildAssetContainer.addAllToScene();
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      // Other

      if (performCameraAnimation) {
        if (animateCamera) {
          const animLength = Camera.animateTo(
            answerCameraPosition,
            answerCameraTarget,
            Config.CAMERA_ANIMATION_LENGTH
          );

          setTimeout(() => {
            Animation.setAnimationStatus(Animation.AnimationStatus.Waiting);
            //Animation.setAnimating(false);
          }, animLength);
        }

        if (automaticallyResetCamera) {
          setTimeout(() => {
            Camera.reset(!animateCamera);
          }, 2000);
        }
      }
    });
}

function getBuildAsset(path: string, isGLB = true): Promise<AssetContainer> {
  return SceneLoader.LoadAssetContainerAsync(
    "/assets/models/",
    path + (isGLB ? ".glb" : ""),
    store.scene
  );
}

function loadBuildAsset(path: string, isGLB = true) {
  getBuildAsset(path, isGLB).then((container) => {
    container.meshes.forEach((mesh) => {
      Tags.AddTagsTo(mesh, "GodRay");
    });

    container.materials.forEach((material) => {
      material.backFaceCulling = false;
      (material as StandardMaterial).emissiveColor = new Color3(1, 1, 1);
    });

    container.animationGroups.forEach((animationGroup) => {
      animationGroup.loopAnimation = false;
    });

    container.addAllToScene();
  });
}

function getAsset(path: string, isGLB = true): Promise<AssetContainer> {
  return SceneLoader.LoadAssetContainerAsync(
    "/assets/models/",
    path + (isGLB ? ".glb" : ""),
    store.scene
  );
}

function loadAsset(path: string, isGLB = true) {
  getAsset(path, isGLB).then((container) => {
    container.meshes.forEach((mesh) => {
      mesh.receiveShadows = true;
      addShadowCaster(mesh as Mesh);

      if (mesh.material) {
        const backgroundMaterial = new BackgroundMaterial(
          "backgroundMaterial",
          store.scene
        );

        backgroundMaterial.diffuseTexture = mesh.material.getActiveTextures()[0];
        backgroundMaterial.shadowLevel = 0.3;
        backgroundMaterial.opacityFresnel = false;

        mesh.material = backgroundMaterial;
      }
    });

    container.addAllToScene();
  });
}

function effectTreeLevel(direction: number) {
  if (direction > 0) {
    increaseTreeLevel();
  } else if (direction < 0) {
    decreaseTreeLevel();
  }
}

function increaseTreeLevel(animate = true) {
  return setTreeLevel(currentTreeLevel + 1, animate);
}

function decreaseTreeLevel(animate = true) {
  return setTreeLevel(currentTreeLevel - 1, animate);
}

function setTreeLevel(level: number, animate = true) {
  if (level !== currentTreeLevel && level > -1 && level < 5) {
    const direction = level > currentTreeLevel ? 1 : -1;

    const from = currentTreeFrame;
    const to = currentTreeFrame + direction;

    if (animate) {
      const temp: AnimationGroup = treeAnimationGroup.clone("TempAnim");

      // Can't see a legit way to do this.
      (temp as any)._from = from;
      (temp as any)._to = to;

      temp.speedRatio = direction;
      temp.play(false);

      temp.onAnimationEndObservable.addOnce(() => {
        temp.dispose();
      });
    } else {
      treeAnimationGroup.play(true);
      treeAnimationGroup.goToFrame(to);
      treeAnimationGroup.pause();
    }

    currentTreeLevel = level;
    currentTreeFrame = to;
  }
}

// if (VueStore.state.isDev) {
//   (window as any).setTreeLevel = setTreeLevel;
//   (window as any).increaseTreeLevel = increaseTreeLevel;
//   (window as any).decreaseTreeLevel = decreaseTreeLevel;
// }

export {
  animating,
  create,
  loadAsset,
  loadBuildAsset,
  processAnswer,
  increaseTreeLevel,
  decreaseTreeLevel,
  effectTreeLevel,
  clearAnswers,
};
